import React, { ReactNode, RefObject, createContext, useContext, useRef, useState } from 'react';
import { CreateReplyMutationVariables } from '__generated__/graphql/legacy/graphql';
import { features } from 'constants/featureFlags';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { sanitizeAttachments } from 'helpers/postHelpers';
import { Control, FieldErrors, UseFormHandleSubmit, UseFormSetValue, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Reply } from 'types/post';
import { EVENT_POST_TYPES, NUMBER_OF_REACTIONS, SUCCESS_TOAST } from 'utils/constants';
import { makeMentionsUserFriendly } from 'utils/mentions';
import { REPLY_FRAGMENT } from 'utils/queries';
import { rawId } from 'utils/rawId';
import { genericToastAlert } from 'utils/toastHelpers';

import { useMutation } from '@apollo/client';
import { toaster } from '@thinkific/toga-react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useCommunityContext } from 'components/CommunityContext';
import { getErrorMap } from 'components/Forms/utils/defaultErrorMap';
import { RichTextEditorHandle } from 'components/RichTextEditor/RichTextEditor';
import { analyzeDocumentFormatting } from 'components/new/Posts/hooks/useEditor/helpers';
import { JSONContent } from 'components/new/Posts/hooks/useEditor/useEditor';

import { POST_QUERY } from 'schema/Post/queries';
import { Attachment } from 'schema/Post/types';
import { getSchema } from 'schema/Replies';
import { CREATE_REPLY_MUTATION, EDIT_REPLY_MUTATION } from 'schema/Replies/mutations';

interface ReplyEditorContextProviderProps {
  children: ReactNode;
}

export interface FormValues {
  content: string;
  body: {
    jsonContent: JSONContent | null;
    plainText: string | null;
  };
  parentPostId?: string;
  parentReplyId?: string;
  attachments: Attachment[];
  mentionedUserIds: string[];
  numberOfReactions: number;
  discussionableId: string;
}

interface ReplyEditorContextType {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  handleSubmit: UseFormHandleSubmit<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  resetForm: () => void;
  onSubmit: (data: FormValues) => void;
  editorRef: RefObject<RichTextEditorHandle>;
  mentionsTextAreaRef: RefObject<HTMLTextAreaElement | HTMLInputElement>;
  isSubmitting: boolean;
  updatePostId: (postId: string) => void;
  updateReplyData: (replyId: string, content: string) => void;
  replyPlaceholder?: string;
  closeReplyOnComment?: () => void;
  isEditing: boolean;
  editingReplyId: string | null;
  startEditing: (comment: Reply) => void;
  cancelEditing: () => void;
}

const ReplyEditorContext = createContext<ReplyEditorContextType>(
  {} as ReplyEditorContextType
);

const useReplyEditorContext = () => {
  return useContext(ReplyEditorContext);
};

const ReplyEditorProvider = ({ children }: ReplyEditorContextProviderProps) => {
  const { community, trackEvent, featureFlags } = useCommunityContext();
  const tiptapEditorEnabled = featureFlags[features.TIPTAP_EDITOR];
  const { t } = useTranslation();
  const [createReplyMutation, { loading: createReplySubmitting }] = useMutation(
    CREATE_REPLY_MUTATION
  );
  const [editReplyMutation, { loading: editReplySubmitting }] = useMutation(
    EDIT_REPLY_MUTATION
  );
  const editorRef = useRef<RichTextEditorHandle>(null);
  const mentionsTextAreaRef = useRef<HTMLTextAreaElement | HTMLInputElement>(
    null
  );

  const [replyPlaceholder, setReplyPlaceholder] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      content: '',
      body: {
        jsonContent: null,
        plainText: '',
      },
      attachments: [],
      mentionedUserIds: [] as string[],
      parentPostId: '',
      parentReplyId: '',
      numberOfReactions: 0,
      discussionableId: community.id,
    },
    ...(tiptapEditorEnabled
      ? { resolver: zodResolver(getSchema(t), { errorMap: getErrorMap(t) }) }
      : {}),
  });

  const getReplyPlaceholder = (content: string) => {
    const transformedContent = makeMentionsUserFriendly(content);
    return transformedContent.length > 60
      ? `${transformedContent.slice(0, 60)}...`
      : transformedContent || 'Attachments';
  };

  const startEditing = (comment: Reply) => {
    setIsEditing(true);
    setEditingReplyId(comment.id);
    setValue('content', comment.content || '');
    setValue('body', {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      jsonContent: (comment?.body?.jsonContent || null) as any,
      plainText: comment?.body?.plainText || null,
    });
    setValue('attachments', (comment.postAttachments as Attachment[]) || []);
    setValue(
      'mentionedUserIds',
      comment.mentionedUsers?.edges?.map((user) => user?.node?.id || '') || []
    );
    setReplyPlaceholder(
      getReplyPlaceholder(comment.content || comment?.body?.plainText || '')
    );
    mentionsTextAreaRef.current?.focus();
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingReplyId(null);
    resetForm();
    mentionsTextAreaRef.current?.focus();
  };

  const resetForm = () => {
    setValue('content', '');
    setValue('body', {
      jsonContent: null,
      plainText: '',
    });
    setValue('attachments', []);
    setValue('mentionedUserIds', []);
    setValue('parentReplyId', '');
    closeReplyOnComment();
  };

  const updatePostId = (postId: string) => {
    setValue('parentPostId', postId);
  };

  const updateReplyData = (replyId: string, content: string) => {
    setValue('parentReplyId', replyId);
    setReplyPlaceholder(getReplyPlaceholder(content));
    mentionsTextAreaRef.current?.focus();
  };

  const closeReplyOnComment = () => {
    setValue('parentReplyId', '');
    setReplyPlaceholder('');
  };

  const onSubmit = async (data) => {
    const {
      content,
      body,
      parentReplyId,
      parentPostId,
      attachments,
      mentionedUserIds,
    } = data;

    if (isEditing) {
      const variables = {
        input: {
          id: editingReplyId,
          content,
          body: tiptapEditorEnabled && body.jsonContent ? body : null,
          attachments: sanitizeAttachments(attachments),
          mentionedUserIds,
        },
        numberOfReactions: NUMBER_OF_REACTIONS,
      };

      try {
        const { data: editReplyData, errors: editReplyError } = await editReplyMutation({
          variables,
        });
        const userErrors = editReplyData?.createPost?.userErrors;
        if (!userErrors?.length && !editReplyError?.length) {
          cancelEditing();
          sendEditTrackingEvent(data);
          resetForm();
          toaster.notice(t(SUCCESS_TOAST.EDIT_REPLY));
        } else {
          genericToastAlert();
        }
      } catch (e) {
        genericToastAlert();
      }
    } else {
      const variables: CreateReplyMutationVariables = {
        discussionableId: community.id,
        content,
        body: body.jsonContent ? body : null,
        [parentReplyId ? 'parentReplyId' : 'parentPostId']:
          parentReplyId || parentPostId,
        // @ts-ignore
        attachments: sanitizeAttachments(attachments),
        mentionedUserIds,
        numberOfReactions: NUMBER_OF_REACTIONS,
      };
      try {
        const {
          data: createReplyData,
          errors: createReplyError,
        } = await createReplyMutation({
          variables,
          refetchQueries: [
            {
              query: POST_QUERY,
              variables: {
                communityId: community.id,
                postId: parentPostId,
                numberOfReactions: NUMBER_OF_REACTIONS,
              },
            },
          ],
          update(cache, { data: { createReply } }) {
            const { reply } = createReply;
            const parentObject = {
              id: parentReplyId || parentPostId,
              __typename: parentReplyId ? 'Reply' : 'Post',
            };
            cache.modify({
              id: cache.identify(parentObject),
              fields: {
                replies(existingReplies = {}) {
                  const newReplyRef = cache.writeFragment({
                    data: reply,
                    fragment: REPLY_FRAGMENT,
                    fragmentName: 'ReplyFields',
                  });
                  const newEdges = [
                    ...(existingReplies.edges || []),
                    { node: newReplyRef },
                  ];
                  const newTotalCount = existingReplies.totalCount + 1;
                  return {
                    ...existingReplies,
                    totalCount: newTotalCount,
                    edges: newEdges,
                  };
                },
              },
            });
          },
        });

        const userErrors = createReplyData?.createPost?.userErrors;
        console.log(userErrors, createReplyError);
        if (!userErrors?.length && !createReplyError?.length) {
          toaster.notice(t(SUCCESS_TOAST.CREATE_REPLY));
          sendCreateTrackingEvent(data);

          resetForm();

          editorRef.current?.reset();
        } else {
          genericToastAlert();
        }
      } catch (e) {
        genericToastAlert();
      }
    }
  };

  const sendCreateTrackingEvent = (data) => {
    const { parentPostId, parentReplyId, attachments, mentionedUserIds } = data;
    const {
      ATTACHMENTS,
      POST_TYPE,
      POST_ID,
      POST_ID_ENCODED,
      HAS_MENTION,
      HAS_RICH_TEXT,
      FORMATTING_TYPES,
    } = TrackingProperty;
    const formattingTypes = analyzeDocumentFormatting(data.body?.jsonContent);

    trackEvent(TrackingEvent.REPLY_CREATED, {
      [ATTACHMENTS]: attachments?.map(
        (attachment) => attachment.attachmentType
      ),
      [POST_ID]: parentReplyId ? rawId(parentReplyId) : rawId(parentPostId),
      [POST_ID_ENCODED]: parentReplyId || parentPostId,
      [POST_TYPE]: parentReplyId
        ? EVENT_POST_TYPES.REPLY
        : EVENT_POST_TYPES.COMMENT,
      [HAS_MENTION]: !!mentionedUserIds?.length,
      [HAS_RICH_TEXT]: formattingTypes.hasRichText,
      [FORMATTING_TYPES]: formattingTypes.formattingTypes,
    });
  };

  const sendEditTrackingEvent = (data) => {
    const { parentPostId, parentReplyId, attachments, mentionedUserIds } = data;
    const {
      ATTACHMENTS,
      COMMENT_ID,
      COMMENT_ID_ENCODED,
      POST_TYPE,
      POST_ID,
      POST_ID_ENCODED,
      HAS_MENTION,
    } = TrackingProperty;

    trackEvent(TrackingEvent.EDIT_REPLY, {
      [ATTACHMENTS]: attachments?.map(
        (attachment) => attachment.attachmentType
      ),
      [COMMENT_ID]: rawId(editingReplyId || ''),
      [COMMENT_ID_ENCODED]: editingReplyId || '',
      [POST_ID]: parentReplyId ? rawId(parentReplyId) : rawId(parentPostId),
      [POST_ID_ENCODED]: parentReplyId || parentPostId,
      [POST_TYPE]: parentReplyId
        ? EVENT_POST_TYPES.REPLY
        : EVENT_POST_TYPES.COMMENT,
      [HAS_MENTION]: !!mentionedUserIds?.length,
    });
  };

  return (
    <ReplyEditorContext.Provider
      value={{
        control,
        handleSubmit,
        errors,
        setValue,
        onSubmit,
        resetForm,
        editorRef,
        isSubmitting: createReplySubmitting || editReplySubmitting,
        updatePostId,
        updateReplyData,
        replyPlaceholder,
        closeReplyOnComment,
        isEditing,
        editingReplyId,
        startEditing,
        cancelEditing,
        mentionsTextAreaRef,
      }}
    >
      {children}
    </ReplyEditorContext.Provider>
  );
};

export { ReplyEditorContext, ReplyEditorProvider, useReplyEditorContext };